import {
  Box,
  CloseButton,
  Flex,
  Group,
  Button as MantineButton,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { ConfirmModal } from 'src/design-system/ConfirmModal';
import { Button } from 'src/design-system/Button';
import { CashflowField, CashflowFields } from './CashflowFields';
import { AllocationConfig, MemberAllocations } from './MemberAllocations';

// Mantine converts a number-like `size` with rem() and turns anything else into
// `var(--modal-size-<value>)`, so a CSS expression here resolves to nothing and the modal
// silently falls back to 440px. The cap lives on `content` instead, which also has to override
// its default `flex: 0 0 var(--modal-size)` before it will shrink at all.
const CARD_WIDTH = 912;

type PreviewRow = { label: string; qualifier?: string; value: string };

/**
 * Offered only when an item already exists, and it removes the item outright rather than the
 * adviser's view of it: a shared item is one item, so every member's copy goes. `message` is what
 * the warning says, which is where the members losing it are named.
 */
type DeletionConfig = {
  onDelete: () => Promise<void>;
  title: string;
  message: string;
  /** Names what is going, e.g. "Delete expenditure". */
  confirmLabel: string;
};

type CashflowModalShellProps = {
  opened: boolean;
  onClose: () => void;
  title: string;
  submitLabel: string;
  onSubmit: () => void;
  canSubmit: boolean;
  isSaving?: boolean;
  previewTitle: string;
  previewRows: PreviewRow[];
  /** Shown in place of the preview rows when the amounts cannot be converted. */
  previewNote?: string | null;
  testId: string;
  fields: CashflowField[][];
  allocation: AllocationConfig;
  /** Sits under the title, explaining why part of the form is read-only. */
  notice?: string;
  /** Absent while adding, since there is nothing yet to delete. */
  deletion?: DeletionConfig;
};

export function CashflowModalShell({
  opened,
  onClose,
  title,
  submitLabel,
  onSubmit,
  canSubmit,
  isSaving = false,
  previewTitle,
  previewRows,
  previewNote,
  testId,
  fields,
  allocation,
  notice,
  deletion,
}: CashflowModalShellProps) {
  const [confirmOpened, confirm] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<{ message: string } | null>(null);

  const handleDelete = async () => {
    if (!deletion || isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deletion.onDelete();
      confirm.close();
    } catch {
      // The caller reports the failure its own way; the dialog stays open so the adviser can
      // see the item is still there and try again rather than assuming it went.
      setDeleteError({ message: 'Could not delete this entry. Try again.' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size={CARD_WIDTH}
      variant="multi"
      styles={{
        content: {
          backgroundColor: 'transparent',
          flex: '0 1 auto',
          maxWidth: 'calc(100vw - 48px)',
        },
      }}
    >
      {/* The panes run to the card's edge, so the card clips them with its own radius rather
          than padding them in and leaving a white gutter around the grey. */}
      <Flex
        w="100%"
        // Floor rather than a fixed height, capped to the viewport so a short screen shrinks the
        // card instead of pushing a scrollbar behind it.
        mih="min(600px, calc(100vh - 48px))"
        // Bounded as well as floored: without a ceiling the card grows with the form and takes
        // the page scrollbar with it, instead of the panes scrolling inside a fixed card.
        mah="calc(100vh - 48px)"
        bg="white"
        style={{ borderRadius: '32px 32px 52px 52px', overflow: 'hidden' }}
        data-testid={testId}
      >
        {/* Both panes split the card evenly and scroll on their own, so they always span the
            same height, and `miw` keeps the floor at the width their fields read at. The basis
            has to be 0 on both: a 400px basis here with the preview growing from 0 handed the
            surplus entirely to the preview and left the halves unequal. */}
        <Box flex="1 1 0" miw={400} style={{ overflowY: 'auto', minHeight: 0 }}>
          <Box pt={32} pb={12} px={32}>
            <Text c="black" fw={FONT_WEIGHTS.semibold} size={'displaySm'}>
              {title}
            </Text>
            {notice && (
              <Text c="gray.7" fw={FONT_WEIGHTS.medium} fz={14} lh="20px" mt={8}>
                {notice}
              </Text>
            )}
          </Box>
          <Stack px={32} pt={12} w="100%">
            <CashflowFields rows={fields} />
            <MemberAllocations {...allocation} />
          </Stack>
        </Box>

        <Flex
          flex="1 1 0"
          miw={400}
          bg="gray.0"
          p={32}
          direction="column"
          pos="relative"
          style={{ minHeight: 0, overflowY: 'auto' }}
        >
          <CloseButton
            onClick={onClose}
            radius="xl"
            size="md"
            bg="gray.10"
            c="white"
            pos="absolute"
            top={16}
            right={16}
            aria-label="Close"
          />

          <Text c="gray.10" fw={FONT_WEIGHTS.semibold} fz={16} lh="24px" mb={16}>
            {previewTitle}
          </Text>
          {previewNote ? (
            <Text c="gray.6" fw={FONT_WEIGHTS.medium} fz={14} lh="20px">
              {previewNote}
            </Text>
          ) : (
            <Stack gap={12} w="100%">
              {previewRows.map(row => (
                <Group
                  key={`${row.label} ${row.qualifier ?? ''}`}
                  justify="space-between"
                  wrap="nowrap"
                >
                  <Text c="gray.7" fw={FONT_WEIGHTS.medium} fz={14} lh="20px">
                    {row.label}
                    {row.qualifier ? ` ${row.qualifier}` : ''}
                  </Text>
                  <Text c="gray.10" fw={FONT_WEIGHTS.semibold} fz={14} lh="20px">
                    {row.value}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}

          <Box mt="auto" pt={32}>
            <Button
              fullWidth
              radius="xl"
              onClick={onSubmit}
              disabled={!canSubmit}
              loading={isSaving}
              data-testid="submit-cashflow"
            >
              {submitLabel}
            </Button>
            {deletion && (
              <MantineButton
                fullWidth
                variant="subtle"
                color="red"
                radius="xl"
                mt={8}
                onClick={confirm.open}
                disabled={isSaving}
                data-testid="delete-cashflow"
              >
                Delete
              </MantineButton>
            )}
          </Box>
        </Flex>
      </Flex>

      {deletion && (
        <ConfirmModal
          opened={confirmOpened}
          onClose={confirm.close}
          onConfirm={handleDelete}
          title={deletion.title}
          description={deletion.message}
          confirmLabel={deletion.confirmLabel}
          error={deleteError}
          isLoading={isDeleting}
        />
      )}
    </Modal>
  );
}
